/**
 * Schedule timezone helper: single source of truth is UTC on the server.
 * - User sets schedule in their local time (browser = user timezone).
 * - We convert local → UTC when saving and show "Stored as: X UTC" in the UI.
 */

/**
 * Convert a schedule from local time (browser) to UTC.
 * Uses a reference date so that "Monday 15:00 local" gives correct UTC hour and day.
 * @param {string} period - 'daily' | 'weekly' | 'monthly' | 'yearly'
 * @param {number} localHour - 0-23
 * @param {number} [localDayOfWeek] - 0-6 (Sunday=0), for weekly
 * @param {number} [localDayOfMonth] - 1-31, for monthly/yearly
 * @returns {{ utcHour: number, utcDayOfWeek?: number, utcDayOfMonth?: number, utcMonth?: number }}
 */
export function localScheduleToUtc(period, localHour, localDayOfWeek = 1, localDayOfMonth = 1) {
  const now = new Date();
  if (period === 'daily') {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), localHour, 0, 0, 0);
    return { utcHour: d.getUTCHours() };
  }
  if (period === 'weekly') {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), localHour, 0, 0, 0);
    const currentDay = d.getDay();
    const daysToAdd = (localDayOfWeek - currentDay + 7) % 7;
    d.setDate(d.getDate() + daysToAdd);
    return { utcHour: d.getUTCHours(), utcDayOfWeek: d.getUTCDay() };
  }
  if (period === 'monthly' || period === 'yearly') {
    const d = new Date(now.getFullYear(), now.getMonth(), localDayOfMonth, localHour, 0, 0, 0);
    return {
      utcHour: d.getUTCHours(),
      utcDayOfMonth: d.getUTCDate(),
      ...(period === 'yearly' && { utcMonth: d.getUTCMonth() }),
    };
  }
  return { utcHour: localHour };
}

/**
 * Convert stored UTC schedule to local time (browser) for display in the "your local time" input.
 * @param {string} period - 'daily' | 'weekly' | 'monthly' | 'yearly'
 * @param {number} utcHour - 0-23
 * @param {number} [utcDayOfWeek] - 0-6, for weekly
 * @param {number} [utcDayOfMonth] - 1-31, for monthly/yearly
 * @param {number} [utcMonth] - 0-11, for yearly
 * @returns {{ localHour: number, localDayOfWeek?: number, localDayOfMonth?: number }}
 */
export function utcScheduleToLocal(
  period,
  utcHour,
  utcDayOfWeek = 1,
  utcDayOfMonth = 1,
  utcMonth = 0,
) {
  const now = new Date();
  if (period === 'daily') {
    const d = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        utcHour,
        0,
        0,
        0,
      ),
    );
    return { localHour: d.getHours() };
  }
  if (period === 'weekly') {
    const utcDate = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      0,
      0,
      0,
      0,
    ));
    const dayOffset = (utcDayOfWeek - utcDate.getUTCDay() + 7) % 7;
    utcDate.setUTCDate(utcDate.getUTCDate() + dayOffset);
    utcDate.setUTCHours(utcHour, 0, 0, 0);
    const d = new Date(utcDate.getTime());
    return { localHour: d.getHours(), localDayOfWeek: d.getDay() };
  }
  if (period === 'monthly' || period === 'yearly') {
    const d = new Date(Date.UTC(now.getUTCFullYear(), period === 'yearly' ? utcMonth : now.getUTCMonth(), utcDayOfMonth, utcHour, 0, 0, 0));
    return { localHour: d.getHours(), localDayOfMonth: d.getDate() };
  }
  return { localHour: utcHour };
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Format stored UTC schedule for display: "14:00 UTC" or "Monday 14:00 UTC", etc.
 * @param {Array<{label:string,value:number}>} [dayOfWeekOptions] - optional, for weekly label
 */
export function formatStoredUtcSchedule(
  period,
  utcHour,
  utcDayOfWeek,
  utcDayOfMonth,
  dayOfWeekOptions,
) {
  const hourStr = `${String(utcHour).padStart(2, '0')}:00 UTC`;
  if (period === 'daily') return hourStr;
  if (period === 'weekly' && utcDayOfWeek !== undefined) {
    const label = dayOfWeekOptions?.find((o) => o.value === utcDayOfWeek)
      ?.label ?? DAY_NAMES[utcDayOfWeek];
    return `${label} ${hourStr}`;
  }
  if ((period === 'monthly' || period === 'yearly') && utcDayOfMonth !== undefined) {
    return `${utcDayOfMonth}. ${hourStr}`;
  }
  return hourStr;
}
